import { Box, Flex, Stack, Text, Title } from '@mantine/core';
import { createStyles } from '@mantine/emotion';
import type React from 'react';

export interface TFEmptyStateMessageProps {
	icon: React.ReactElement;
	title: string;
	description?: string;
	technicalNote?: string;
	primaryAction?: React.ReactNode;
	secondaryAction?: React.ReactNode;
	isLarge?: boolean;
}

const useStyles = createStyles(({ colors }) => ({
	icon: {
		width: 48,
		height: 48,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 12,
		backgroundColor: colors.gray[1],
		'& > *': {
			width: 32,
			height: 32,
			strokeWidth: '1.5px',
		},
	},
	largeIcon: {
		width: 56,
		height: 56,
		borderRadius: 16,
		'& > *': {
			width: 40,
			height: 40,
			strokeWidth: '2px',
		},
	},
	title: {
		color: colors.gray[9],
		fontSize: 18,
		lineHeight: 1.11,
		marginTop: 16,
		textAlign: 'center',
	},
	largeTitle: {
		fontSize: 24,
		lineHeight: 1.333,
		marginTop: 24,
	},
	description: {
		marginTop: 12,
		color: colors.gray[6],
		fontSize: 14,
		lineHeight: 1.429,
		textAlign: 'center',
	},
	largeDescription: {
		fontSize: 16,
		marginTop: 16,
		lineHeight: 1.5,
	},
	technicalNote: {
		color: colors.gray[4],
		fontSize: 12,
		marginTop: 12,
		lineHeight: 1.5,
		textAlign: 'center',
	},
}));

export const TFEmptyStateMessage = ({
	icon,
	title,
	description,
	technicalNote,
	primaryAction,
	secondaryAction,
	isLarge = false,
}: TFEmptyStateMessageProps) => {
	const { classes, cx } = useStyles();
	const isActionsExist = primaryAction || secondaryAction;

	return (
		<Stack my="4rem" align="center" gap="0">
			<Box className={cx(classes.icon, { [classes.largeIcon]: isLarge })}>{icon}</Box>
			<Title order={4} className={cx(classes.title, { [classes.largeTitle]: isLarge })}>
				{title}
			</Title>
			{description && (
				<Text className={cx(classes.description, { [classes.largeDescription]: isLarge })}>{description}</Text>
			)}
			{technicalNote && <Text className={classes.technicalNote}>{technicalNote}</Text>}
			{isActionsExist && (
				<Flex gap={8} mt={isLarge ? 32 : 24}>
					{primaryAction}
					{secondaryAction}
				</Flex>
			)}
		</Stack>
	);
};
